import get from 'lodash/get'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { Identifier, Pagination, Record, RecordMap, ReduxState, Sort } from '../types'
import { recordsToRecordMap } from './recordsToRecordMap'
import useQueryWithStore, { QueryOptions } from './useQueryWithStore'

const defaultData = {}
const defaultIds: Identifier[] = []

/**
 * Call the dataProvider.getList() method and return the resolved result
 * as well as the loading state.
 *

 *
 * The return value updates according to the request state:
 *
 * - start: { loading: true, loaded: false }
 * - success: { data: [data from store], ids: [ids from response], total: [total from response], loading: false, loaded: true }
 * - error: { error: [error from response], loading: false, loaded: true }
 *
 * This hook will return the cached result when called a second time
 * with the same parameters, until the response arrives.
 *
 * @param {string} resource The resource name, e.g. 'posts'
 * @param {Object} pagination The request pagination { page, perPage }, e.g. { page: 1, perPage: 10 }
 * @param {Object} sort The request sort { field, order }, e.g. { field: 'id', order: 'DESC' }
 * @param {Object} filter The request filters, e.g. { title: 'hello, world' }
 * @param {Object} options Options object to pass to the dataProvider. May include side effects to be executed upon success of failure, e.g. { onSuccess: { refresh: true } }
 *
 * @returns The current request state. Destructure as { data, total, ids, error, loading, loaded }.
 *
 * @example
 *
 * import { useGetList } from 'react-admin';
 *
 * const LatestNews = () => {
 *     const { data, ids, loading, error } = useGetList(
 *         'posts',
 *         { page: 1, perPage: 10 },
 *         { field: 'published_at', order: 'DESC' }
 *     );
 *     if (loading) { return <Loading />; }
 *     if (error) { return <p>ERROR</p>; }
 *     return <ul>{ids.map(id =>
 *         <li key={id}>{data[id].title}</li>
 *     )}</ul>;
 * };
 */

interface FilterType {
  auto_string?: string
  [key: string]: any
}

const useGetListHandler = <RecordType extends Record = Record>(
  resource: string,
  pagination: Pagination,
  sort: Sort,
  filter: FilterType,
  options?: QueryOptions
) => {
  const requestSignature = JSON.stringify({ pagination, sort, filter })
  const requestSignatureAll = JSON.stringify({ filter })

  const { data: ids = defaultIds, total, error, loading, loaded } = useQueryWithStore(
    { type: 'getList', resource, payload: { pagination, sort, filter } },
    options,
    // data selector (may return [])
    (state: ReduxState): Identifier[] =>
      get(state.admin.resources, [resource, 'list', 'cachedRequests', requestSignature, 'ids'], defaultIds),
    // total selector (may return undefined)
    (state: ReduxState): number =>
      get(state.admin.resources, [resource, 'list', 'cachedRequests', requestSignatureAll, 'total'])
  )

  const resourceData = useSelector((state: ReduxState): ReduxState['admin']['resources'] => {
    if (!ids) return defaultData
    return state.admin.resources
  })

  const data = useMemo(() => {
    if (!ids) return defaultData
    const allResourceData = get(resourceData, [resource, 'data'], defaultData)
    const records = ids.map((id) => allResourceData[id]).filter(Boolean)
    return recordsToRecordMap<RecordType>(records)
  }, [ids, resourceData, resource])

  return { data, ids, total, error, loading, loaded }
}

const useGetList = <RecordType extends Record = Record>(
  resource: string,
  pagination: Pagination,
  sort: Sort,
  filter: FilterType,
  options?: QueryOptions
): {
  data?: RecordMap<RecordType>
  ids?: Identifier[]
  total?: number
  error?: any
  loading: boolean
  loaded: boolean
} => {
  return useGetListHandler<RecordType>(resource, pagination, sort, filter, options)
}

export default useGetList
